export class DataSet implements IDataSet {
   public sortColumn: string = 'Name'
   public sortDirection: ODataSortDirection = 'asc'
   public filters: ODataFilter[] = []
   public query: NameValuePair[] = []
   public expand: string[] = []
   public loading: boolean = false

   private _currentPageNumber: number = 1
   private _pageSize: number = 10
   private _items: any[] = []
   private _totalItemCount: number = 0

   constructor(private datasource: IDataSource) {}

   public get items() {
      return this._items
   }

   public get itemCount() {
      return this._items.length
   }

   public get totalItemCount() {
      return this._totalItemCount
   }

   public get lastPageNumber() {
      return Math.max(1, Math.floor((this.totalItemCount - 1) / this.pageSize) + 1)
   }

   public get hasNextPage() {
      return this.currentPageNumber < this.lastPageNumber
   }

   public get hasPreviousPage() {
      return this.currentPageNumber > 1
   }

   public get firstItemIndex() {
      return this.pageSize * (this.currentPageNumber - 1)
   }

   public get lastItemIndex() {
      return Math.min(this.firstItemIndex + this.pageSize - 1, this.totalItemCount - 1)
   }

   public get firstItemNumber() {
      return this.firstItemIndex + 1
   }

   public get lastItemNumber() {
      return this.lastItemIndex + 1
   }

   public get currentPageNumber() {
      return this._currentPageNumber
   }
   public get pageSize() {
      return this._pageSize
   }

   public get isEmpty() {
      return this.itemCount === 0 && !this.loading
   }

   public filterFunction: (items: any[]) => any[] = items => items

   public async load(): Promise<IDataSourceResult> {
      try {
         this.loading = true
         const result = await this.datasource.fill(this)
         this.fill(result.items, result.count)
         return result
      } catch {
         this.loading = false
         return { items: [], count: 0 } as IDataSourceResult
      }
   }

   public nextPage() {
      if (this.hasNextPage) {
         this._currentPageNumber = this.currentPageNumber + 1
      }
   }
   public previousPage() {
      if (this.hasPreviousPage) {
         this._currentPageNumber = this.currentPageNumber - 1
      }
   }
   public showPage(pageNumber: number) {
      if (pageNumber >= 1 && pageNumber <= this.lastPageNumber) {
         this._currentPageNumber = pageNumber
      }
   }

   public sort(columnName: string) {
      if (this.sortColumn === columnName) {
         if (this.sortDirection === 'asc') {
            this.sortDirection = 'desc'
         } else {
            this.sortDirection = 'asc'
         }
      } else {
         this.sortColumn = columnName
         this.sortDirection = 'asc'
      }
   }

   public updateFilter(filterName: string, filterValue: string | string[]) {
      const filter = this.filters.find(f => f.name === filterName)
      if (filter) {
         filter.value = filterValue
      }
   }

   public addQuery(queryName: string, queryValue: string) {
      const item = this.query.find(q => q.name === queryName)
      if (item) {
         item.value = queryValue
      } else {
         this.query.push({ name: queryName, value: queryValue })
      }
   }

   public removeQuery(queryName: string) {
      const item = this.query.find(q => q.name === queryName)
      if (item) {
         const index = this.query.indexOf(item, 0)
         if (index > -1) {
            this.query.splice(index, 1)
         }
      }
   }

   public updateQuery(queryName: string, queryValue: string) {
      const query = this.query.find(q => q.name === queryName)
      if (query) {
         query.value = queryValue
      }
   }
   public setPageSize(pageSize: number) {
      this._pageSize = pageSize
      if (this.currentPageNumber > this.lastPageNumber) {
         this.showPage(this.lastPageNumber)
      }
   }

   private fill(newItems: any[], totalItemCount: number) {
      this.loading = false
      const filteredItems = this.filterFunction(newItems)
      this._items = filteredItems
      this._totalItemCount = totalItemCount
   }
}
